import React, { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import './QRScanner.css';

const QRGenerator = ({ 
  profileId, 
  profileName = '', 
  thaaliNo = '',
  isOpen, 
  onClose,
  size = 256 
}) => {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);
  const printAreaRef = useRef(null);

  // Generate QR code when component opens or profileId changes
  useEffect(() => {
    if (isOpen && profileId) {
      generateQRCode();
    }
  }, [isOpen, profileId]);

  const generateQRCode = async () => {
    if (!profileId) {
      setError('ITS ID is required');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      // Generate QR code with high error correction for better scanning
      const qrOptions = {
        errorCorrectionLevel: 'H', // High error correction
        type: 'image/png',
        quality: 0.92,
        margin: 2,
        color: {
          dark: '#000000',  // Black dots
          light: '#FFFFFF'  // White background
        },
        width: size
      };

      const dataUrl = await QRCode.toDataURL(profileId, qrOptions);
      setQrDataUrl(dataUrl);

      // Also draw to canvas for higher quality printing
      if (canvasRef.current) {
        await QRCode.toCanvas(canvasRef.current, profileId, {
          ...qrOptions,
          width: 300 // Higher resolution for printing
        });
      }

    } catch (err) {
      console.error('QR Generation Error:', err);
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Sticker - ${profileName || profileId}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: 4in 4in;
              margin: 0.1in;
            }
            
            body {
              font-family: 'Arial', sans-serif;
              width: 4in;
              height: 4in;
              display: flex;
              align-items: center;
              justify-content: center;
              background: white;
              margin: 0;
              padding: 0;
            }
            
            .sticker-container {
              width: 3.8in;
              height: 3.8in;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              text-align: center;
              border: 2px solid #C38154;
              border-radius: 0.2in;
              background: white;
              padding: 0.15in;
              position: relative;
            }
            
            .header-section {
              width: 100%;
              margin-bottom: 0.1in;
            }
            
            .organization-name {
              font-size: 14px;
              font-weight: bold;
              color: #C38154;
              line-height: 1.1;
              margin-bottom: 0.05in;
            }
            
            .profile-info {
              font-size: 11px;
              color: #333;
              line-height: 1.2;
              margin-bottom: 0.1in;
              word-wrap: break-word;
              max-width: 100%;
            }
            
            .qr-section {
              flex-grow: 1;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0.1in 0;
            }
            
            .qr-image {
              width: 2.2in;
              height: 2.2in;
              border: 1px solid #ddd;
              border-radius: 0.1in;
              background: white;
            }
            
            .thaali-number {
              font-family: 'Arial', sans-serif;
              font-size: 20px;
              font-weight: bold;
              color: #C38154;
              background: #f8f9fa;
              border: 2px solid #C38154;
              border-radius: 0.1in;
              padding: 0.08in 0.12in;
              margin-top: 0.1in;
              letter-spacing: 1px;
              min-width: 0.8in;
              text-align: center;
            }
            
            .footer-note {
              position: absolute;
              bottom: 0.05in;
              left: 50%;
              transform: translateX(-50%);
              font-size: 8px;
              color: #999;
              white-space: nowrap;
            }
            
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .sticker-container {
                border: 2px solid #C38154 !important;
                background: white !important;
              }
              
              .thaali-number {
                background: #f8f9fa !important;
                border: 2px solid #C38154 !important;
              }
              
              .footer-note {
                display: none;
              }
            }
            
            /* Responsive adjustments for very long names */
            @media (max-width: 4in) {
              .profile-info {
                font-size: 10px;
              }
              
              .qr-image {
                width: 2in;
                height: 2in;
              }
              
              .thaali-number {
                font-size: 18px;
              }
            }
          </style>
        </head>
        <body>
          <div class="sticker-container">
            <div class="header-section">
              <div class="organization-name">FMB - Istifadah Ilmiyah</div>
              ${profileName ? `<div class="profile-info">${profileName} - ${profileId}</div>` : '<div class="profile-info">ITS</div>'}
            </div>
            
            <div class="qr-section">
              <img src="${qrDataUrl}" alt="QR Code" class="qr-image" />
            </div>
            
            <div class="thaali-number">${thaaliNo}</div>
            
            <div class="footer-note">Handle with care</div>
          </div>
          
          <script>
            window.onload = function() {
              // Small delay to ensure styles are loaded
              setTimeout(() => {
                window.print();
                window.onafterprint = function() {
                  window.close();
                };
              }, 500);
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const downloadQR = () => {
    if (!qrDataUrl) return;

    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `qr-code-${profileId}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRegenerateQR = () => {
    generateQRCode();
  };

  if (!isOpen) return null;

  return (
    <div className="qr-modal-overlay" onClick={onClose}>
      <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="qr-modal-header">
          <h2>Generate QR Code</h2>
          <button className="qr-close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="qr-modal-body">
          {/* Profile Information */}
          <div className="qr-profile-info">
            <div className="qr-profile-item">
              <span className="qr-label">ITS ID:</span>
              <span className="qr-value">{profileId}</span>
            </div>
            {profileName && (
              <div className="qr-profile-item">
                <span className="qr-label">Name:</span>
                <span className="qr-value">{profileName}</span>
              </div>
            )}
          </div>

          {/* QR Code Display */}
          <div className="qr-display-area">
            {isGenerating ? (
              <div className="qr-loading">
                <div className="qr-spinner"></div>
                <p>Generating QR Code...</p>
              </div>
            ) : error ? (
              <div className="qr-error">
                <p>{error}</p>
                <button className="qr-retry-btn" onClick={handleRegenerateQR}>
                  Try Again
                </button>
              </div>
            ) : qrDataUrl ? (
              <div className="qr-preview">
                <div className="qr-preview-container">
                  <img src={qrDataUrl} alt="QR Code" className="qr-image" />
                  <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>
                <div className="qr-preview-info">
                  <p className="qr-preview-text">
                    QR Code for its: <strong>{profileId}</strong>
                  </p>
                  <p className="qr-instructions">
                    This QR code will identify this specific tiffin when scanned.
                    Print and attach securely to the tiffin container.
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          {/* Action Buttons */}
          {qrDataUrl && !isGenerating && (
            <div className="qr-actions">
              <button className="qr-print-btn" onClick={handlePrint}>
                🖨️ Print QR Code
              </button>
              <button className="qr-download-btn" onClick={downloadQR}>
                💾 Download PNG
              </button>
              <button className="qr-regenerate-btn" onClick={handleRegenerateQR}>
                🔄 Regenerate
              </button>
            </div>
          )}
        </div>

        <div className="qr-modal-footer">
          <div className="qr-footer-info">
            <p>💡 <strong>Tip:</strong> Use waterproof labels for better durability</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRGenerator;